<template>
  <div class="min-h-screen bg-gray-50 pb-12 font-sans">
    <!-- Admin Intro Panel -->
    <div v-if="active" class="bg-gray-900 text-white py-8 mb-0 border-b border-white/5">
      <div class="max-w-[1400px] mx-auto px-4 sm:px-6">
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div class="max-w-3xl">
            <h2 class="text-xl font-black mb-2 flex items-center gap-2">
              <div class="w-2 h-2 rounded-full bg-brand animate-pulse"></div>
              {{ t('agent.profile_preview') }}
            </h2>
            <p class="text-gray-400 text-sm font-bold leading-relaxed whitespace-pre-line">
              {{ t('agent.intro') }}
            </p>
          </div>
          <div class="flex items-center gap-6 bg-white/5 p-6 rounded-2xl border border-white/10 backdrop-blur-xl">
            <div class="flex flex-col">
              <span class="text-xs font-black text-white uppercase tracking-[0.2em] text-brand mb-1">{{ t('agent.make_public') }}</span>
              <span class="text-[10px] text-gray-500 font-bold">{{ t('agent.pricing') }}</span>
            </div>
            <button
              type="button"
              class="relative inline-flex h-8 w-14 items-center rounded-full transition-all duration-300 focus:outline-none ring-offset-gray-900"
              :class="publicPage ? 'bg-orange-500' : 'bg-gray-700'"
              @click="onPageStatus(!publicPage)"
            >
              <span
                class="inline-block h-6 w-6 transform rounded-full bg-white shadow-xl transition-transform duration-300 ease-spring"
                :class="publicPage ? 'translate-x-7' : 'translate-x-1'"
              />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Cover & Profile Header -->
    <div class="relative h-68 md:h-80 bg-gradient-to-br from-brand/5 via-gray-100 to-orange-100 shadow-inner border-b border-gray-200">
      <div v-if="agent?.openroom?.cover?.url" class="absolute inset-0 overflow-hidden">
        <img :src="agent.openroom.cover.url" class="w-full h-full object-cover" />
        <div class="absolute inset-0 bg-black/30"></div>
      </div>

      <!-- Banner Actions -->
      <div v-if="active" class="absolute top-8 right-8 z-30 flex gap-3">
        <UIUpload v-slot="{ upload }" accept="image/*" @files="onUploadCover">
          <button type="button" class="bg-white/90 hover:bg-white text-gray-900 px-6 py-3 rounded-2xl text-xs font-black shadow-2xl backdrop-blur-md transition-all flex items-center gap-2 border border-white/20 active:scale-95 uppercase tracking-widest" @click="upload">
            <Pencil :size="16" /> {{ t('agent.upload_cover') }}
          </button>
        </UIUpload>
        <button v-if="agent?.openroom?.cover?.url" type="button" class="bg-red-500 hover:bg-red-600 text-white p-3 rounded-2xl shadow-2xl transition-all active:scale-95 border border-white/20" @click="onDeleteCover">
          <Trash2 :size="18" />
        </button>
      </div>

      <!-- Profile Info Overlay -->
      <div class="absolute bottom-1 md:bottom-12 left-0 right-0 sm:px-6 z-10">
        <div class="max-w-[1400px] px-4 sm:px-6 mx-auto flex flex-col md:flex-row items-start gap-6">
          <div class="relative group">
            <ImageWithFallback
              :src="agentAvatar"
              :alt="agent?.personal?.fullname"
              class="w-32 h-32 md:w-40 md:h-40 rounded-2xl border-4 border-white shadow-2xl object-cover bg-white ring-1 ring-black/5"
            />
            <div v-if="active" class="absolute inset-0 flex items-center justify-center bg-black/40 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-300 backdrop-blur-sm overflow-hidden">
              <UIUpload v-slot="{ upload }" accept="image/*" @files="onUploadAvatar">
                <div class="flex flex-col items-center gap-2">
                  <button type="button" class="bg-white p-4 rounded-2xl text-brand shadow-xl hover:scale-110 transition-transform active:scale-95" @click="upload">
                    <Pencil :size="24" />
                  </button>
                  <button v-if="agent?.avatar?.url || agent?.avatar?.data" type="button" class="text-white hover:text-red-400 text-xs font-black uppercase tracking-widest transition-colors mt-2" @click="onDeleteAvatar">
                    {{ t('agent.cancel') }}
                  </button>
                </div>
              </UIUpload>
            </div>
          </div>
          <div class="flex-1 pb-4">
            <h1 class="text-3xl md:text-4xl font-black text-gray-900 tracking-tight drop-shadow-sm" :class="[agent?.openroom?.cover?.url && 'text-white drop-shadow-md']">
              {{ agent?.personal?.fullname }}
            </h1>
            <p v-if="agent?.personal?.family_name_furigana" class="text-brand font-bold bg-brand/5 border border-brand/10 backdrop-blur-md px-3 py-1 rounded-lg inline-block mt-2 text-xs uppercase tracking-[0.1em]">
              {{ agent.personal.family_name_furigana }} {{ agent.personal.given_name_furigana }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="max-w-[1400px] mx-auto px-4 sm:px-6 mt-4 relative z-0">
      <div class="flex flex-col lg:flex-row gap-10">
        <!-- Left Column: Tabs & Content -->
        <div class="flex-1 min-w-0">
          <!-- Tabs -->
          <div class="flex border-b border-gray-200 mb-8 bg-white rounded-t-2xl px-2 md:px-6 overflow-x-auto scrollbar-hide shadow-sm sticky top-20 z-20">
            <button
              v-for="tab in ['profile', 'listing', 'team']"
              :key="tab"
              type="button"
              class="px-6 md:px-8 py-5 text-sm font-black transition-all relative whitespace-nowrap uppercase tracking-widest flex items-center gap-2"
              :class="activeTab === tab ? 'text-brand' : 'text-gray-400 hover:text-gray-600'"
              @click="activeTab = tab"
            >
              <User v-if="tab === 'profile'" :size="18" />
              <Building v-if="tab === 'listing'" :size="18" />
              <Users v-if="tab === 'team'" :size="18" />
              {{ t(`agent.tabs.${tab}`) }}
              <div v-if="activeTab === tab" class="absolute bottom-0 left-0 right-0 h-1 bg-brand rounded-t-full"></div>
            </button>
          </div>

          <!-- Tab Content Area -->
          <div class="bg-white rounded-b-2xl p-6 md:p-12 shadow-sm min-h-[600px] border border-gray-100/50">
            <!-- Profile Tab -->
            <div v-if="activeTab === 'profile'" class="space-y-16 text-gray-700">
              <!-- Message Section -->
              <UIEditable v-slot="{ editing, set }">
                <section>
                  <div class="flex justify-between items-center mb-8">
                    <div class="flex items-center gap-4">
                      <div class="bg-brand/10 p-2.5 rounded-xl text-brand">
                        <Quote :size="20" />
                      </div>
                      <h2 class="text-2xl font-black text-gray-900 tracking-tight">
                        {{ t('agent.fields.message') }}
                      </h2>
                    </div>
                    <div v-if="active" class="shrink-0">
                      <button v-if="!editing" type="button" class="text-brand hover:bg-brand/5 px-4 py-2 rounded-xl flex items-center gap-2 font-bold text-sm border-2 border-brand/10 transition-all" @click="set(true)">
                        <Pencil :size="16" /> {{ t('agent.edit') }}
                      </button>
                      <div v-else class="flex gap-3">
                        <button type="button" class="text-gray-400 hover:text-gray-600 text-sm font-bold px-3 py-2 transition-colors" @click="onCancel(set)">{{ t('agent.cancel') }}</button>
                        <button type="button" class="bg-brand text-white px-6 py-2 rounded-xl text-sm font-bold flex items-center gap-2 shadow-lg shadow-brand/20 transition-all active:scale-95" @click="onSave(set, ['personal.message'])">
                          <Save :size="16" /> {{ t('agent.save') }}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div v-if="editing" class="animate-in fade-in duration-300">
                    <textarea
                      v-model="editData.personal.message"
                      class="w-full p-6 border-2 border-gray-100 rounded-2xl focus:border-brand focus:ring-0 outline-none min-h-[200px] shadow-inner bg-gray-50 transition-all text-lg leading-relaxed font-medium"
                    ></textarea>
                  </div>
                  <div v-else class="bg-gray-50/50 p-8 rounded-2xl border-2 border-gray-100/50 relative overflow-hidden group">
                    <Quote class="absolute -top-6 -right-6 text-brand/5 w-32 h-32 group-hover:scale-110 transition-transform duration-700" />
                    <p v-if="agent?.personal?.message" class="leading-loose text-lg text-gray-600 font-bold relative z-10">
                      {{ agent.personal.message }}
                    </p>
                    <p v-else class="text-gray-400 italic font-bold text-center py-4">{{ t('agent.no_message') }}</p>
                  </div>
                </section>
              </UIEditable>

              <!-- Main Details Grid -->
              <UIEditable v-slot="{ editing, set }">
                <div class="border-t-2 border-gray-50 pt-16 relative">
                  <div v-if="active" class="absolute top-4 right-0">
                    <button v-if="!editing" type="button" class="text-brand hover:bg-brand/5 px-4 py-2 rounded-xl flex items-center gap-2 font-bold text-sm border-2 border-brand/10 transition-all" @click="set(true)">
                      <Pencil :size="16" /> {{ t('agent.edit') }}
                    </button>
                    <div v-else class="flex gap-3">
                      <button type="button" class="text-gray-400 hover:text-gray-600 text-sm font-bold px-3 py-2" @click="onCancel(set)">{{ t('agent.cancel') }}</button>
                      <button type="button" class="bg-brand text-white px-6 py-2 rounded-xl text-sm font-bold flex items-center gap-2 shadow-lg shadow-brand/20 transition-all active:scale-95" @click="onSave(set, ['personal.specialties', 'personal.certificates', 'personal.languages', 'personal.areas'])">
                        <Save :size="16" /> {{ t('agent.save') }}
                      </button>
                    </div>
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-16">
                    <!-- Specialties -->
                    <section>
                      <div class="flex items-center gap-3 mb-6">
                        <div class="w-2 h-8 bg-blue-500 rounded-full"></div>
                        <h3 class="font-black text-gray-900 uppercase tracking-tight">{{ t('agent.fields.specialties') }}</h3>
                      </div>
                      <div v-if="editing">
                        <UITagList
                          :tags="specialtyOptions"
                          :value="editData.personal.specialties"
                          @change="editData.personal.specialties = $event"
                        />
                      </div>
                      <div v-else class="flex flex-wrap gap-3">
                        <template v-if="agent?.personal?.specialties?.length">
                          <span
                            v-for="s in agent.personal.specialties"
                            :key="s"
                            class="px-4 py-2 bg-blue-50 text-blue-700 rounded-xl text-sm font-black border border-blue-100 transition-all hover:bg-blue-100"
                          >
                            {{ t(`agent.specialties.${s}`) }}
                          </span>
                        </template>
                        <p v-else class="text-gray-400 italic font-bold text-sm">{{ t('agent.not_specified') }}</p>
                      </div>
                    </section>

                    <!-- Languages -->
                    <section>
                      <div class="flex items-center gap-3 mb-6">
                        <div class="w-2 h-8 bg-green-500 rounded-full"></div>
                        <h3 class="font-black text-gray-900 uppercase tracking-tight">{{ t('agent.fields.languages') }}</h3>
                      </div>
                      <div v-if="editing" class="space-y-3">
                        <div v-for="(l, index) in editData.personal.languages" :key="index" class="flex gap-2 items-center bg-gray-50 p-2 rounded-xl border border-gray-200">
                          <select
                            :value="l.split('/')[0]"
                            class="flex-1 p-2 bg-white rounded-lg text-sm font-bold outline-none border-none focus:ring-2 focus:ring-brand/20"
                            @change="updateLanguage(index, 'lang', ($event.target as HTMLSelectElement).value)"
                          >
                            <option v-for="opt in languageOptions" :key="opt.id" :value="opt.id">{{ opt.label }}</option>
                          </select>
                          <select
                            :value="l.split('/')[1]"
                            class="flex-1 p-2 bg-white rounded-lg text-sm font-bold outline-none border-none focus:ring-2 focus:ring-brand/20"
                            @change="updateLanguage(index, 'level', ($event.target as HTMLSelectElement).value)"
                          >
                            <option v-for="opt in levelOptions" :key="opt.id" :value="opt.id">{{ opt.label }}</option>
                          </select>
                          <button type="button" class="text-red-400 hover:bg-red-50 p-2 rounded-lg transition-colors" @click="removeLanguage(index)"><Trash2 :size="18" /></button>
                        </div>
                        <button type="button" class="w-full py-3 border-2 border-dashed border-gray-200 rounded-2xl text-gray-400 hover:border-brand hover:text-brand hover:bg-brand/5 transition-all flex justify-center items-center gap-2 text-xs font-black uppercase tracking-widest" @click="addLanguage">
                          <Plus :size="18" /> Add Language
                        </button>
                      </div>
                      <div v-else class="flex flex-wrap gap-3">
                        <template v-if="agent?.personal?.languages?.length">
                          <span
                            v-for="l in agent.personal.languages"
                            :key="l"
                            class="px-4 py-2 bg-green-50 text-green-700 rounded-xl text-sm font-black border border-green-100 transition-all hover:bg-green-100"
                          >
                            {{ formatLanguage(l) }}
                          </span>
                        </template>
                        <p v-else class="text-gray-400 italic font-bold text-sm">{{ t('agent.not_specified') }}</p>
                      </div>
                    </section>

                    <!-- Certificates -->
                    <section>
                      <div class="flex items-center gap-3 mb-6">
                        <div class="w-2 h-8 bg-sky-500 rounded-full"></div>
                        <h3 class="font-black text-gray-900 uppercase tracking-tight">{{ t('agent.fields.certificates') }}</h3>
                      </div>
                      <div v-if="editing">
                        <textarea
                          v-model="editData.personal.certificatesStr"
                          placeholder="Enter certificates, one per line"
                          class="w-full p-4 border-2 border-gray-100 rounded-2xl text-sm min-h-[150px] shadow-inner bg-gray-50 focus:border-brand outline-none font-bold"
                          @input="editData.personal.certificates = editData.personal.certificatesStr.split('\n').filter(Boolean)"
                        ></textarea>
                      </div>
                      <div v-else class="bg-sky-50/30 p-8 rounded-2xl border-2 border-sky-100/50">
                        <ul v-if="agent?.personal?.certificates?.length" class="space-y-4">
                          <li v-for="c in agent.personal.certificates" :key="c" class="text-gray-700 font-bold flex items-center gap-4 group">
                            <div class="bg-white p-1.5 rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                              <Award class="text-sky-500 w-4 h-4 shrink-0" />
                            </div>
                            {{ c }}
                          </li>
                        </ul>
                        <p v-else class="text-gray-400 italic font-bold text-sm text-center">{{ t('agent.not_specified') }}</p>
                      </div>
                    </section>

                    <!-- Areas -->
                    <section>
                      <div class="flex items-center gap-3 mb-6">
                        <div class="w-2 h-8 bg-orange-500 rounded-full"></div>
                        <h3 class="font-black text-gray-900 uppercase tracking-tight">{{ t('agent.fields.areas') }}</h3>
                      </div>
                      <div v-if="editing">
                        <div class="flex gap-2 mb-5">
                          <input
                            v-model="newArea"
                            placeholder="Add new area..."
                            class="flex-1 p-3 border-2 border-gray-100 rounded-xl text-sm font-bold focus:border-brand outline-none transition-all shadow-sm"
                            @keyup.enter="addArea"
                          />
                          <button type="button" class="bg-brand text-white p-3 rounded-xl shadow-lg shadow-brand/20 active:scale-95 transition-all" @click="addArea"><Plus :size="20" /></button>
                        </div>
                        <div class="flex flex-wrap gap-3">
                          <span v-for="a in editData.personal.areas" :key="a" class="group flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-xs font-black shadow-sm">
                            {{ a }}
                            <button type="button" class="text-gray-300 hover:text-red-500 transition-colors" @click="removeArea(a)"><X :size="16" /></button>
                          </span>
                        </div>
                      </div>
                      <div v-else class="flex flex-wrap gap-3">
                        <template v-if="agent?.personal?.areas?.length">
                          <span
                            v-for="a in agent.personal.areas"
                            :key="a"
                            class="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-black border border-gray-200/50 hover:bg-brand/5 hover:text-brand hover:border-brand/20 transition-all cursor-default"
                          >
                            {{ a }}
                          </span>
                        </template>
                        <p v-else class="text-gray-400 italic font-bold text-sm">{{ t('agent.not_specified') }}</p>
                      </div>
                    </section>
                  </div>
                </div>
              </UIEditable>

              <!-- Additional Details Section -->
              <UIEditable v-slot="{ editing, set }">
                <section class="border-t-2 border-gray-50 pt-16">
                  <div class="flex justify-between items-center mb-12">
                    <div class="flex items-center gap-4">
                      <div class="bg-brand p-2.5 rounded-xl text-white shadow-lg">
                        <SquareUserRound :size="20" />
                      </div>
                      <h3 class="text-2xl font-black text-gray-900 tracking-tight uppercase">
                        {{ t('agent.personal_details') }}
                      </h3>
                    </div>
                    <div v-if="active">
                      <button v-if="!editing" type="button" class="text-brand hover:bg-brand/5 px-4 py-2 rounded-xl flex items-center gap-2 font-bold text-sm border-2 border-brand/10 transition-all" @click="set(true)">
                        <Pencil :size="16" /> {{ t('agent.edit') }}
                      </button>
                      <div v-else class="flex gap-3">
                        <button type="button" class="text-gray-400 hover:text-gray-600 text-sm font-bold px-3 py-2 transition-colors" @click="onCancel(set)">{{ t('agent.cancel') }}</button>
                        <button type="button" class="bg-brand text-white px-6 py-2 rounded-xl text-sm font-bold flex items-center gap-2 shadow-lg shadow-brand/20 transition-all active:scale-95" @click="onSave(set, ['personal.motto', 'personal.freeTime', 'personal.hobby', 'personal.skill', 'personal.birthplace'])">
                          <Save :size="16" /> {{ t('agent.save') }}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-12">
                    <div v-for="field in ['motto', 'freeTime', 'hobby', 'skill', 'birthplace']" :key="field" class="group bg-gray-50/30 p-6 rounded-2xl border border-gray-100 hover:bg-white hover:shadow-xl hover:shadow-black/5 transition-all duration-500">
                      <h4 class="text-[10px] uppercase font-black text-gray-400 mb-3 tracking-[0.2em] transition-colors group-hover:text-brand">{{ t(`agent.fields.${field}`) }}</h4>
                      <div v-if="editing" class="mt-2">
                        <input
                          v-model="editData.personal[field]"
                          class="w-full p-3 border-2 border-gray-100 rounded-2xl text-sm font-bold focus:border-brand outline-none transition-all shadow-inner bg-white"
                        />
                      </div>
                      <p v-else class="text-lg font-black text-gray-800 leading-tight">
                        {{ agent?.personal?.[field] || t('agent.not_specified') }}
                      </p>
                    </div>
                  </div>
                </section>
              </UIEditable>
            </div>

            <!-- Listing Tab -->
            <div v-if="activeTab === 'listing'" class="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div v-if="loadingListings && !listings.length" class="flex justify-center py-32">
                <LoadingIndicator />
              </div>
              <div v-else-if="listings.length">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-10">
                  <PropertyCard
                    v-for="p in listings"
                    :key="p.id"
                    v-bind="p"
                    photo-priority="zumen"
                  />
                </div>

                <!-- Load More Button -->
                <div v-if="hasMoreListings" class="mt-16 flex justify-center">
                  <button
                    type="button"
                    :disabled="loadingListings"
                    class="bg-white hover:bg-gray-50 text-gray-900 px-10 h-16 rounded-2xl font-black shadow-lg shadow-black/5 transition-all active:scale-95 border-2 border-gray-100 flex items-center gap-3 uppercase tracking-widest text-xs disabled:opacity-50"
                    @click="fetchListings(true)"
                  >
                    <LoadingIndicator v-if="loadingListings" class="!w-4 !h-4" />
                    <Plus v-else :size="18" />
                    {{ t('agent.load_more') }}
                  </button>
                </div>
              </div>
              <div v-else class="flex flex-col items-center justify-center py-32 bg-gray-50 rounded-2xl border-4 border-dashed border-gray-100/80 group">
                <div class="bg-white p-8 rounded-2xl shadow-xl mb-8 group-hover:scale-110 transition-all duration-700">
                  <Building class="w-16 h-16 text-gray-200 group-hover:text-brand/20 transition-colors" />
                </div>
                <p class="text-gray-400 font-black text-xl text-center px-12 tracking-tight">{{ t('agent.no_listings') }}</p>
              </div>
            </div>

            <!-- Team Tab -->
            <div v-if="activeTab === 'team'" class="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div v-if="team.length" class="grid grid-cols-1 gap-8">
                <div
                  v-for="member in team"
                  :key="member.id"
                  class="flex flex-col sm:flex-row items-center gap-8 p-10 border-2 border-gray-50 rounded-2xl hover:border-brand/10 hover:shadow-2xl transition-all duration-500 bg-white group"
                >
                  <div class="relative shrink-0">
                    <ImageWithFallback
                      :src="getAvatarUrl(member)"
                      class="w-28 h-24 rounded-2xl object-cover border-4 border-white shadow-xl group-hover:rotate-3 transition-transform"
                    />
                    <div class="absolute inset-0 rounded-2xl ring-2 ring-brand/0 group-hover:ring-brand/10 transition-all"></div>
                  </div>
                  <div class="flex-1 min-w-0 text-center sm:text-left">
                    <h3 class="font-black text-3xl text-gray-900 group-hover:text-brand transition-colors truncate tracking-tighter">{{ member.personal?.fullname }}</h3>
                    <p class="text-sm text-gray-400 font-black uppercase tracking-[0.2em] mt-2">{{ member.company?.general?.name || company?.name }}</p>
                    <div class="flex flex-wrap justify-center sm:justify-start gap-2 mt-6">
                      <span v-for="s in member.personal?.specialties?.slice(0, 3)" :key="s" class="text-[10px] uppercase font-black tracking-widest px-4 py-1.5 bg-gray-50 rounded-full text-gray-400 group-hover:bg-brand group-hover:text-white transition-all duration-500">
                        {{ t(`agent.specialties.${s}`) }}
                      </span>
                    </div>
                  </div>
                  <Button
                    as-child="a"
                    :href="`/agent/${pageContext.routeParams.organizationId}/${member.id}`"
                    variant="outline"
                    class="rounded-2xl px-10 h-14 font-black shrink-0 hover:bg-gray-900 hover:text-white border-2 border-gray-100 hover:border-gray-900 transition-all uppercase tracking-widest text-xs"
                  >
                    {{ t('agent.viewProfile') }}
                  </Button>
                </div>
              </div>
              <div v-else class="flex flex-col items-center justify-center py-32 bg-gray-50 rounded-2xl border-4 border-dashed border-gray-100/80 group">
                <div class="bg-white p-8 rounded-2xl shadow-xl mb-8 group-hover:scale-110 transition-all duration-700">
                  <Users class="w-16 h-16 text-gray-200 group-hover:text-brand/20 transition-colors" />
                </div>
                <p class="text-gray-400 font-black text-xl text-center px-12 tracking-tight">{{ t('agent.no_team') }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column: Sidebar -->
        <div class="w-full lg:w-[380px] space-y-10">
          <!-- Company Card -->
          <div class="bg-white rounded-2xl border-2 border-gray-50 p-10 shadow-sm sticky top-32 transition-all duration-500 hover:shadow-2xl">
            <div v-if="companyLogo" class="mb-12 flex justify-center p-6 bg-gray-50/50 rounded-2xl border border-gray-100 shadow-inner group overflow-hidden ring-1 ring-black/5">
              <img :src="companyLogo" :alt="company?.name" class="max-h-28 object-contain group-hover:scale-110 transition-transform duration-1000" />
            </div>

            <div class="text-center">
              <h2 class="text-2xl font-black text-gray-900 mb-2 tracking-tighter leading-none">{{ company?.name }}</h2>
              <p v-if="company?.branch" class="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] mb-10">{{ company.branch }}</p>
              <div v-else class="mb-10"></div>
            </div>

            <div class="space-y-10">
              <div class="flex items-start gap-6 group">
                <div class="bg-brand/5 p-4 rounded-2xl text-brand group-hover:bg-brand group-hover:text-white transition-all duration-500 shadow-sm">
                  <MapPin :size="24" />
                </div>
                <div class="min-w-0">
                  <p class="font-black text-gray-400 uppercase text-[9px] tracking-[0.2em] mb-2 group-hover:text-brand transition-colors">{{ t('agent.fields.officeAddress') }}</p>
                  <p class="text-gray-700 font-bold leading-relaxed text-sm">{{ company?.address || agent?.company?.address || session.address }}</p>
                </div>
              </div>

              <div v-if="company?.phone" class="flex items-start gap-6 group">
                <div class="bg-brand/5 p-4 rounded-2xl text-brand group-hover:bg-brand group-hover:text-white transition-all duration-500 shadow-sm">
                  <Phone :size="24" />
                </div>
                <div class="min-w-0">
                  <p class="font-black text-gray-400 uppercase text-[9px] tracking-[0.2em] mb-2 group-hover:text-brand transition-colors">{{ t('agent.fields.directLine') }}</p>
                  <a :href="`tel:${company.phone}`" class="text-gray-900 font-black hover:text-brand transition-colors tracking-tighter leading-none">{{ company.phone }}</a>
                </div>
              </div>
            </div>

            <div class="mt-14 space-y-4">
              <Button
                v-if="company?.website"
                as-child="a"
                :href="company.website"
                target="_blank"
                variant="outline"
                class="w-full justify-center px-8 h-16 rounded-2xl font-black text-gray-600 hover:text-gray-800 border-2 border-gray-300 group hover:text-brand hover:bg-brand/5 transition-all uppercase tracking-widest text-xs"
              >
                <Globe :size="20" class="mr-3 text-gray-600 group-hover:text-gray-800 transition-colors" />
                {{ t('agent.visitWebSite') }}
              </Button>
              <Button
                v-if="mapUrl"
                as-child="a"
                :href="mapUrl"
                target="_blank"
                variant="outline"
                class="w-full justify-center px-8 h-16 rounded-2xl font-black text-gray-600 hover:text-gray-800 border-2 border-gray-300 group hover:text-brand hover:bg-brand/5 transition-all uppercase tracking-widest text-xs"
              >
                <MapPin :size="20" class="mr-3 text-gray-600 group-hover:text-gray-800 transition-colors" />
                {{ t('agent.showOnMap') }}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { usePageContext } from 'vike-vue/usePageContext';
import { useI18n } from 'vue-i18n';
import { MapPin, Phone, Globe, Pencil, X, Save, Trash2, Plus, Quote, Award, Building, Users, User, SquareUserRound } from 'lucide-vue-next';
import { ImageWithFallback, PropertyCard, Button, LoadingIndicator, useAPI } from '@repo/ui';
import { useSessionStore } from '@/stores/session';
import UIEditable from '@/components/UIEditable.vue';
import UITagList from '@/components/UITagList.vue';
import UIUpload from '@/components/UIUpload.vue';

const pageContext = usePageContext();
const { t } = useI18n();
const api = useAPI();
const session = useSessionStore();

// Pre-populate store for SSR consistency
session.agent = pageContext.data.agent;
session.organization = pageContext.data.company;
session.members = pageContext.data.team || [];

const agent = computed(() => session.agent);
const company = computed(() => session.organization.general);
const team = computed(() => (session.members || []).filter(a => a.id !== pageContext.routeParams.id));
const active = computed(() => session.active);
const publicPage = computed(() => session.publicPage);

import type { Deal, UserProfile } from '@repo/types'

const activeTab = ref('profile');
const listings = ref<Deal[]>([]);
const loadingListings = ref(false);
const listingsCount = ref(0);
const nextIndex = ref(0);
const hasMoreListings = computed(() => listingsCount.value > nextIndex.value);

const agentAvatar = computed(() => session.agentAvatar);
const companyLogo = computed(() => session.companyLogo);
const mapUrl = computed(() => session.mapUrl);

// --- EDIT LOGIC ---
const editData = ref<UserProfile>({
  personal: {
    message: '',
    specialties: [],
    certificates: [],
    certificatesStr: '',
    languages: [],
    areas: [],
    motto: '',
    freeTime: '',
    hobby: '',
    skill: '',
    birthplace: ''
  }
});

watch(agent, (newAgent) => {
  if (newAgent?.personal) {
    editData.value.personal = {
      ...newAgent.personal,
      certificatesStr: newAgent.personal.certificates?.join('\n') || ''
    };
  }
}, { immediate: true, deep: true });

async function updateProfile(update: Record<string, string | null>) {
  try {
    await api.call('updateProfile', update);
    if (session.agent) {
      for (const [key, value] of Object.entries(update)) {
        const path = key.split('.');
        let target: object = session.agent;
        for (let i = 0; i < path.length - 1; i++) {
          if (!target[path[i]]) target[path[i]] = {};
          target = target[path[i]];
        }
        target[path[path.length - 1]] = value;
      }
    }
  } catch (e) {
    console.error('Update failed', e);
  }
}

async function onPageStatus(value: boolean) {
  const status = value ? 'public' : 'private';
  await updateProfile({ 'openroom.status': status });
}

async function onUploadCover(files: FileList) {
  try {
    const { data } = await api.call('upload', 'profile-pictures', files[0]);
    await updateProfile({ 'openroom.cover': data });
  } catch (err) {
    console.error('Cover upload failed', err);
  }
}

async function onDeleteCover() {
  await updateProfile({ 'openroom.cover.url': null, 'openroom.cover.key': null });
}

async function onUploadAvatar(files: FileList) {
  try {
    const { data } = await api.call('upload', 'profile-pictures', files[0]);
    await updateProfile({ 'avatar': data });
  } catch (err) {
    console.error('Avatar upload failed', err);
  }
}

async function onDeleteAvatar() {
  await updateProfile({ 'avatar.key': null, 'avatar.url': null });
}

async function onSave(set: (v: boolean) => void, fields: string[]) {
  const update: Record<string, string> = {};
  for (const field of fields) {
    const key = field.split('.').pop() as string;
    update[field] = (editData.value.personal as UserProfile)[key];
  }
  await updateProfile(update);
  set(false);
}

function onCancel(set: (v: boolean) => void) {
  if (session.agent) {
    editData.value.personal = {
      ...session.agent.personal,
      certificatesStr: session.agent.personal.certificates?.join('\n') || ''
    };
  }
  set(false);
}

const specialtyOptions = ['rent', 'sale', 'investment'].map((id) => ({
  id,
  label: t('agent.specialties.' + id),
}));

const languageOptions = ['ja', 'en', 'zh', 'ko', 'vi', 'tgl', 'pt', 'fr'].map(id => ({
  id,
  label: t('agent.languages.' + id)
}));

const levelOptions = ['native', 'professional', 'conversational'].map(id => ({
  id,
  label: t('agent.languages.' + id)
}));

const newArea = ref('');
function addArea() {
  if (!newArea.value) return;
  if (!editData.value.personal.areas) editData.value.personal.areas = [];
  editData.value.personal.areas.push(newArea.value);
  newArea.value = '';
}
function removeArea(area: string) {
  editData.value.personal.areas = editData.value.personal.areas?.filter(a => a !== area);
}

function addLanguage() {
  if (!editData.value.personal.languages) editData.value.personal.languages = [];
  editData.value.personal.languages.push('ja/native');
}
function updateLanguage(index: number, part: 'lang' | 'level', value: string) {
  const current = editData.value.personal.languages![index].split('/');
  if (part === 'lang') current[0] = value;
  else current[1] = value;
  editData.value.personal.languages![index] = current.join('/');
}
function removeLanguage(index: number) {
  editData.value.personal.languages?.splice(index, 1);
}

function formatLanguage(val: string) {
  const [lang, level] = val.split('/');
  return `${t(`agent.languages.${lang || 'ja'}`)} (${t(`agent.languages.${level || 'conversational'}`)})`;
}

async function fetchListings(more = false) {
  if (!more && listings.value.length > 0) return;
  if (loadingListings.value) return;

  loadingListings.value = true;
  try {
    const { id, organizationId } = pageContext.routeParams;
    const skip = more ? nextIndex.value : 0;
    const { data } = await api.call('agentProperties', id, organizationId, skip);

    const newBuildings = data.buildings || [];
    if (more) {
      listings.value = [...listings.value, ...newBuildings];
    } else {
      listings.value = newBuildings;
    }

    listingsCount.value = data.dealsCount || 0;
    nextIndex.value = data.nextIndex || 0;
  } catch (e) {
    console.error('Failed to fetch listings', e);
  } finally {
    loadingListings.value = false;
  }
}

watch(activeTab, (newTab) => {
  if (newTab === 'listing') {
    fetchListings();
  }
});

const syncData = async () => {
  // Sync store with fresh data on client
  const { agent, company, team } = pageContext.data;
  session.agent = agent;
  session.organization = company;
  session.members = team || [];

  // Reset listings
  listings.value = [];
  listingsCount.value = 0;
  nextIndex.value = 0;
  if (activeTab.value === 'listing') {
    fetchListings();
  }

  // Robust session check from original
  const re = /(^|;)\s*openroom_forest_exp\s*=\s*([^;]+)/;
  const match = document.cookie.match(re);
  if (match?.length) {
    const flag = match.pop() as string;
    const [, organizationId] = flag.split('_');
    if (organizationId == pageContext.routeParams.organizationId) {
      try {
        await api.call('refresh', organizationId, import.meta.env.PUBLIC_ENV__APP_ID);
        const { data: meData } = await api.call('me');
        session.active = meData.login.id.toString() == pageContext.routeParams.id;
      } catch (err) {
        console.error('Session verification failed', err);
        session.active = false;
      }
    } else {
      session.active = false;
    }
  } else {
    session.active = false;
  }
};

onMounted(syncData);
watch(() => pageContext.routeParams.id, syncData);

function getAvatarUrl(user: UserProfile) {
  if (user.avatar?.url) return user.avatar.url;
  if (user.avatar?.data) return 'data:image/png;base64,' + user.avatar.data;
  return '/images/avatar.svg';
}
</script>

<style scoped>
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
